import {
  BYTES_PER_MEGABYTE,
  DURATION_MS_PER_SECOND,
  INITIAL_ASSET_CATALOG_FILTERS,
} from '@/features/admin-dashboard/config/assetCatalog';
import { toAssetCatalogRequestFilters } from '@/features/admin-dashboard/lib/assetCatalog';
import type {
  AssetCatalogFilterState,
  AssetCatalogSearch,
} from '@/features/admin-dashboard/types/assetCatalog';
import { normalizeAssetKeyQuery } from '@/features/assets/lib/assetKeyQuery';
import {
  ASSET_METADATA_FIELD_KEYS,
  type AssetCatalogFilters,
  type AssetMetadataFieldKey,
  type AssetSort,
} from '@/features/assets/types/asset';
import { z } from 'zod';

const metadataSearchShape = Object.fromEntries(
  ASSET_METADATA_FIELD_KEYS.flatMap((key) => [
    [`metadata.${key}`, z.string().min(1).optional().catch(undefined)],
    [`metadata.${key}.present`, z.enum(['true', 'false']).optional().catch(undefined)],
  ]),
);

const assetCatalogSearchShape = {
  q: z.string().trim().min(1).optional().catch(undefined),
  organizationId: z.string().min(1).optional().catch(undefined),
  projectId: z.string().min(1).optional().catch(undefined),
  rejected: z.enum(['true', 'false']).optional().catch(undefined),
  minDurationSeconds: z.string().optional().catch(undefined),
  maxDurationSeconds: z.string().optional().catch(undefined),
  minSizeMegabytes: z.string().optional().catch(undefined),
  maxSizeMegabytes: z.string().optional().catch(undefined),
  delivery: z.string().min(1).optional().catch(undefined),
  deliveryOperator: z.enum(['in', 'not_in']).optional().catch(undefined),
  sort: z.enum(['key', 'filename']).optional().catch(undefined),
  order: z.enum(['asc', 'desc']).optional().catch(undefined),
  ...metadataSearchShape,
};

export const assetCatalogSearchSchema = z.object(
  assetCatalogSearchShape,
) as z.ZodType<AssetCatalogSearch>;

function metadataValueKey(key: AssetMetadataFieldKey) {
  return `metadata.${key}` as const;
}

function metadataPresenceKey(key: AssetMetadataFieldKey) {
  return `metadata.${key}.present` as const;
}

export function searchToFilterState(search: AssetCatalogSearch): AssetCatalogFilterState {
  const metadataValues: AssetCatalogFilterState['metadataValues'] = {};
  const metadataPresence: AssetCatalogFilterState['metadataPresence'] = {};

  for (const key of ASSET_METADATA_FIELD_KEYS) {
    const value = search[metadataValueKey(key)];
    const presence = search[metadataPresenceKey(key)];
    if (value !== undefined) metadataValues[key] = value;
    if (presence === 'true') metadataPresence[key] = 'present';
    if (presence === 'false') metadataPresence[key] = 'missing';
  }

  return {
    organizationId: search.organizationId ?? INITIAL_ASSET_CATALOG_FILTERS.organizationId,
    projectId: search.projectId ?? INITIAL_ASSET_CATALOG_FILTERS.projectId,
    rejection:
      search.rejected === 'true' ? 'rejected' : search.rejected === 'false' ? 'active' : 'all',
    minDurationSeconds:
      search.minDurationSeconds ?? INITIAL_ASSET_CATALOG_FILTERS.minDurationSeconds,
    maxDurationSeconds:
      search.maxDurationSeconds ?? INITIAL_ASSET_CATALOG_FILTERS.maxDurationSeconds,
    minSizeMegabytes: search.minSizeMegabytes ?? INITIAL_ASSET_CATALOG_FILTERS.minSizeMegabytes,
    maxSizeMegabytes: search.maxSizeMegabytes ?? INITIAL_ASSET_CATALOG_FILTERS.maxSizeMegabytes,
    metadataValues,
    metadataPresence,
  };
}

/** Reads the active ordering from the URL; direction defaults to the API's ascending order. */
export function searchToAssetSort(search: AssetCatalogSearch): AssetSort {
  if (!search.sort) return null;
  return { field: search.sort, direction: search.order ?? 'asc' };
}

/** Serializes the inseparable sort pair and clears both keys for backend default order. */
export function assetSortToSearchParams(
  sort: AssetSort,
): Pick<AssetCatalogSearch, 'sort' | 'order'> {
  if (!sort) return { sort: undefined, order: undefined };
  return { sort: sort.field, order: sort.direction };
}

/**
 * Serializes the filter panel's state to search params. `q` and the ordering are
 * deliberately not part of the result: the patch is merged over the current params, so a
 * filter change must leave the active search term and sort alone (each has its own patch
 * producer). Every filter key IS present — set to `undefined` when unset — so emptying a
 * filter clears its param.
 */
export function filterStateToSearchParams(
  state: AssetCatalogFilterState,
): Omit<AssetCatalogSearch, 'q' | 'sort' | 'order' | 'delivery' | 'deliveryOperator'> {
  const search: Omit<AssetCatalogSearch, 'q' | 'sort' | 'order' | 'delivery' | 'deliveryOperator'> =
    {
      organizationId: state.organizationId || undefined,
      projectId: state.projectId || undefined,
      rejected:
        state.rejection === 'rejected'
          ? 'true'
          : state.rejection === 'active'
            ? 'false'
            : undefined,
      minDurationSeconds: state.minDurationSeconds || undefined,
      maxDurationSeconds: state.maxDurationSeconds || undefined,
      minSizeMegabytes: state.minSizeMegabytes || undefined,
      maxSizeMegabytes: state.maxSizeMegabytes || undefined,
    };

  for (const key of ASSET_METADATA_FIELD_KEYS) {
    search[metadataValueKey(key)] = state.metadataValues[key]?.trim()
      ? state.metadataValues[key]
      : undefined;
    const presence = state.metadataPresence[key] ?? 'any';
    search[metadataPresenceKey(key)] =
      presence === 'present' ? 'true' : presence === 'missing' ? 'false' : undefined;
  }

  return search;
}

export function searchToRequestFilters(search: AssetCatalogSearch): AssetCatalogFilters {
  return {
    ...toAssetCatalogRequestFilters(
      normalizeAssetKeyQuery(search.q) ?? '',
      searchToFilterState(search),
      DURATION_MS_PER_SECOND,
      BYTES_PER_MEGABYTE,
    ),
    // Included so the route loader warms the sorted first page on a deep link or reload.
    sort: searchToAssetSort(search),
    delivery: search.delivery ?? 'all',
    deliveryOperator: search.deliveryOperator ?? 'in',
    rejected: search.rejected === undefined ? undefined : search.rejected === 'true',
  };
}
